/**
 * NeedsReviewList — Tab „Braucht deinen Blick" der Run-Detail-Seite (T7).
 * Lädt Suggestions (route=needs_review, status=pending) für den Lauf, rendert
 * je Suggestion eine `NeedsReviewCard`, Aktionen Übernehmen (+Undo-Toast,
 * T13/P2)/Verwerfen (Dialog), Pagination, EmptyState.
 *
 * Produktname via `useProductNames` (T15/P2, Batch-Lookup) — Fallback
 * gekürzte `product_id`, solange der Name lädt/fehlt. Channel-Name kommt aus
 * dem bestehenden `useChannels()`-Hook (channels.id → custom_name/channel_name),
 * da `ChannelContentSuggestion.channel_id` die `channels.id`-UUID referenziert
 * (nicht die JTL-`channel_id`, s. Review-Doc F1).
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Inbox } from 'lucide-react'
import { Link } from 'react-router-dom'

import { EmptyState, Pagination } from '@/components/v2/ui'
import { useChannels } from '@/hooks/useChannels'
import { EID } from '@/lib/element-ids.registry'

import { useSuggestions } from '../hooks/useSuggestions'
import { useApplyWithUndo } from '../hooks/useApplyWithUndo'
import { useProductNames } from '../hooks/useProductNames'
import { NeedsReviewCard } from './NeedsReviewCard'
import { DiscardDialog } from './DiscardDialog'

const PAGE_SIZE = 20

export function NeedsReviewList({ runId }: { runId: string }) {
  const { t } = useTranslation()
  const [page, setPage] = useState(1)
  const [discardingId, setDiscardingId] = useState<string | null>(null)
  const { data, isLoading } = useSuggestions({ run_id: runId, route: 'needs_review', status: 'pending', page })
  const { apply, discard, applyWithUndo } = useApplyWithUndo()
  const { data: channels } = useChannels()

  const channelNameById: Record<string, string> = {}
  for (const c of channels ?? []) channelNameById[c.id] = c.custom_name || c.channel_name

  const items = data?.items ?? []
  const { data: productNames } = useProductNames(items.map((s) => s.product_id))
  const productLabel = (id: string) => (
    <Link to={`/v2/products/${id}`} className="hover:text-primary-600 hover:underline dark:hover:text-primary-400">
      {productNames?.products[id] || id.slice(0, 8)}
    </Link>
  )
  const hasNextPage = items.length === PAGE_SIZE
  const totalPages = page + (hasNextPage ? 1 : 0)

  if (isLoading) return null
  if (items.length === 0) {
    return <EmptyState icon={<Inbox className="h-12 w-12" />} title={t('channelContent.ready.allReviewed')} />
  }

  return (
    <div className="text-bulk-review-list space-y-4">
      {items.map((s) => (
        <NeedsReviewCard
          key={s.id}
          suggestion={s}
          channelName={channelNameById[s.channel_id] ?? s.channel_id.slice(0, 8)}
          productLabel={productLabel(s.product_id)}
          busy={
            (apply.isPending && apply.variables?.suggestion_ids.includes(s.id) === true) ||
            (discard.isPending && discardingId === s.id)
          }
          onApply={(editedValue) => applyWithUndo(s.id, editedValue)}
          onDiscard={() => setDiscardingId(s.id)}
        />
      ))}

      {totalPages > 1 && (
        <div
          id={EID['text-bulk-review-pagination-region']}
          className="flex justify-end border-t border-neutral-200 pt-4 dark:border-neutral-800"
        >
          <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
        </div>
      )}

      <DiscardDialog
        open={discardingId !== null}
        busy={discard.isPending}
        onCancel={() => setDiscardingId(null)}
        onConfirm={(reason) => {
          if (!discardingId) return
          discard.mutate({ suggestion_ids: [discardingId], reason })
          setDiscardingId(null)
        }}
      />
    </div>
  )
}
